import { useLayoutEffect, useState } from "react";

import { lineWrapperAt } from "./lineMenuBlocks";

import type { Editor } from "@tiptap/core";

type Box = { top: number; left: number; width: number; height: number };

export type MenuLayout = {
  /** Where the menu button floats, in viewport coordinates. */
  top: number;
  left: number;
  /** The block's line, relative to `root`, for the hover background. */
  line: Box;
};

/** The menu button sits just past the line's right edge, a little above its top. */
const BUTTON_OFFSET = { top: -4, left: 4 };

function isSameLayout(a: MenuLayout | null, b: MenuLayout): boolean {
  return (
    !!a &&
    a.top === b.top &&
    a.left === b.left &&
    a.line.top === b.line.top &&
    a.line.left === b.line.left &&
    a.line.width === b.line.width &&
    a.line.height === b.line.height
  );
}

function measure(editor: Editor, root: HTMLElement, pos: number): MenuLayout | null {
  const line = lineWrapperAt(editor, pos)?.getBoundingClientRect();

  if (!line) {
    return null;
  }

  const rootRect = root.getBoundingClientRect();

  return {
    top: line.top + BUTTON_OFFSET.top,
    left: line.right + BUTTON_OFFSET.left,
    line: {
      top: line.top - rootRect.top,
      left: line.left - rootRect.left,
      width: line.width,
      height: line.height,
    },
  };
}

/** Measures the line of the block at `pos`, again whenever the page scrolls or the editor changes. */
export function useMenuLayout(
  editor: Editor | undefined,
  root: HTMLElement | null,
  pos: number | null,
): MenuLayout | null {
  const [layout, setLayout] = useState<MenuLayout | null>(null);

  // A layout effect, so the menu never paints at the previous block's position.
  useLayoutEffect(() => {
    if (!editor || !root || pos === null) {
      setLayout(null);
      return;
    }

    const remeasure = () => {
      const next = measure(editor, root, pos);
      setLayout((previous) => (next && isSameLayout(previous, next) ? previous : next));
    };

    remeasure();
    window.addEventListener("scroll", remeasure, true);
    window.addEventListener("resize", remeasure);
    editor.on("update", remeasure);
    editor.on("selectionUpdate", remeasure);

    return () => {
      window.removeEventListener("scroll", remeasure, true);
      window.removeEventListener("resize", remeasure);
      editor.off("update", remeasure);
      editor.off("selectionUpdate", remeasure);
    };
  }, [editor, root, pos]);

  return layout;
}
